import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
	setIdForPricePoint,
	setIsPricePointSetFromCookie,
	setKilometrageChoice,
	setPriceForPricePoint,
} from 'store/actions';
import {
	chosenDurationSelector,
	chosenKilometrageSelector,
	leaseCarGroupedPricePointsSelector,
} from 'store/selectors';
import { LeaseCarDataPricePoint } from 'types';
import { getValueFromCookie, setCookie } from 'utils';

export const useDefaultKilometrage = () => {
	const dispatch = useDispatch();
	const selectedKilometrage = useSelector(chosenKilometrageSelector);
	const selectedDuration = useSelector(chosenDurationSelector);
	const groupedPricePoints = useSelector(leaseCarGroupedPricePointsSelector);
	const kmValueFromCookie = getValueFromCookie('kilometrage');

	const [cheapestKilometrage, setCheapestKilometrage] = useState<
		LeaseCarDataPricePoint | undefined
	>();
	const [availableKilometrages, setAvailableKilometrages] = useState<LeaseCarDataPricePoint[]>([]);

	// Find cheapest kilometrage for current duration
	useEffect(() => {
		if (groupedPricePoints && selectedDuration) {
			const pricePointGroup = groupedPricePoints[Number(selectedDuration)];
			if (!pricePointGroup) {
				setAvailableKilometrages([]);
				setCheapestKilometrage(undefined);
				return;
			}

			const { kilometrages } = pricePointGroup;
			const sortedKilometrages = [...kilometrages].sort((a, b) => a.mileage - b.mileage);
			setAvailableKilometrages(sortedKilometrages);
			setCheapestKilometrage(kilometrages.find((km) => km.pricePerMonth === 0));
		}
	}, [groupedPricePoints, selectedDuration]);

	// Set default kilometrage if not already selected
	useEffect(() => {
		if (!selectedKilometrage && selectedDuration && groupedPricePoints) {
			// Check if we have a cookie value
			if (kmValueFromCookie) {
				// Validate cookie value against available kilometrages
				const kilometrageObject = groupedPricePoints[Number(selectedDuration)]?.kilometrages.find(
					(kilometrage) => kilometrage.mileage.toString() === kmValueFromCookie
				);

				if (kilometrageObject) {
					dispatch(setKilometrageChoice(kilometrageObject.mileage.toString()));
					dispatch(setIdForPricePoint(kilometrageObject.id));
					dispatch(setPriceForPricePoint(kilometrageObject.pricePerMonth));
					dispatch(setIsPricePointSetFromCookie(true));
				} else if (cheapestKilometrage) {
					// Fall back to the cheapest kilometrage
					dispatch(setKilometrageChoice(cheapestKilometrage.mileage.toString()));
					dispatch(setIdForPricePoint(cheapestKilometrage.id));
					dispatch(setPriceForPricePoint(cheapestKilometrage.pricePerMonth));
					setCookie('kilometrage', cheapestKilometrage.mileage.toString());
				}
			} else if (cheapestKilometrage) {
				// No cookie, use cheapest
				dispatch(setKilometrageChoice(cheapestKilometrage.mileage.toString()));
				dispatch(setIdForPricePoint(cheapestKilometrage.id));
				dispatch(setPriceForPricePoint(cheapestKilometrage.pricePerMonth));
				setCookie('kilometrage', cheapestKilometrage.mileage.toString());
			}
		}
	}, [
		selectedKilometrage,
		kmValueFromCookie,
		cheapestKilometrage,
		dispatch,
		groupedPricePoints,
		selectedDuration,
	]);

	// Sync cookie value with selected kilometrage
	useEffect(() => {
		if (
			kmValueFromCookie &&
			selectedKilometrage !== kmValueFromCookie &&
			selectedDuration &&
			groupedPricePoints
		) {
			const kilometrageObject = groupedPricePoints[Number(selectedDuration)]?.kilometrages.find(
				(kilometrage) => kilometrage.mileage.toString() === kmValueFromCookie
			);

			if (kilometrageObject) {
				dispatch(setKilometrageChoice(kmValueFromCookie));
				dispatch(setIdForPricePoint(kilometrageObject.id));
				dispatch(setPriceForPricePoint(kilometrageObject.pricePerMonth));
			}
		}
	}, [kmValueFromCookie, selectedKilometrage, selectedDuration, dispatch, groupedPricePoints]);

	return {
		selectedKilometrage:
			kmValueFromCookie || selectedKilometrage || cheapestKilometrage?.mileage.toString() || '',
		availableKilometrages,
		cheapestKilometrage,
		groupedPricePoints,
		selectedDuration,
	};
};
